import React from "react";
import { useTranslation } from "./useTranslation";

const PrivacyPolicy = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t("privacy.title")}
          </h1>
          <p className="text-lg text-gray-700">
            {t("privacy.subtitle")}
          </p>
        </div>

        {/* Last Updated */}
        <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mb-8">
          <p className="text-sm text-gray-800">
            <strong>{t("privacy.lastUpdated.label")}</strong> {t("privacy.lastUpdated.date")}
          </p>
        </div>

        <div className="prose prose-lg max-w-none text-gray-800">
          {/* Introduction */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("privacy.sections.intro.heading")}
            </h2>
            <p className="mb-4 leading-relaxed">
              {t("privacy.sections.intro.p1")}
            </p>
            <p className="leading-relaxed">
              {t("privacy.sections.intro.p2")}
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("privacy.sections.collect.heading")}
            </h2>
            <p className="mb-4">
              {t("privacy.sections.collect.p1")}
            </p>
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {t("privacy.sections.collect.email.title")}
              </h3>
              <p className="mb-3">{t("privacy.sections.collect.email.collectedWhen")}</p>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>{t("privacy.sections.collect.email.items.0")}</li>
                <li>{t("privacy.sections.collect.email.items.1")}</li>
                <li>{t("privacy.sections.collect.email.items.2")}</li>
                <li>{t("privacy.sections.collect.email.items.3")}</li>
              </ul>
              <p className="text-sm text-gray-600">
                <strong>{t("privacy.sections.collect.email.purposeLabel")}</strong>{" "}
                {t("privacy.sections.collect.email.purpose")}
              </p>
            </div>

            <p className="mb-3">
              <strong>{t("privacy.sections.collect.notCollect.title")}</strong>
            </p>
            <ul className="list-disc pl-6 space-y-1 mb-6">
              <li>{t("privacy.sections.collect.notCollect.items.0")}</li>
              <li>{t("privacy.sections.collect.notCollect.items.1")}</li>
              <li>{t("privacy.sections.collect.notCollect.items.2")}</li>
              <li>{t("privacy.sections.collect.notCollect.items.3")}</li>
              <li>{t("privacy.sections.collect.notCollect.items.4")}</li>
            </ul>
          </section>

          {/* Use of Information */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("privacy.sections.use.heading")}
            </h2>
            <p className="mb-4">{t("privacy.sections.use.p1")}</p>
            <ul className="list-disc pl-6 space-y-1 mb-6">
              <li>{t("privacy.sections.use.items.0")}</li>
              <li>{t("privacy.sections.use.items.1")}</li>
              <li>{t("privacy.sections.use.items.2")}</li>
              <li>{t("privacy.sections.use.items.3")}</li>
            </ul>
            <p className="text-sm text-gray-600">
              {t("privacy.sections.use.optOut")}
            </p>
          </section>

          {/* Data Sharing */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("privacy.sections.share.heading")}
            </h2>
            <p className="mb-4">{t("privacy.sections.share.p1")}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>{t("privacy.sections.share.items.0.title")}</strong>{" "}
                {t("privacy.sections.share.items.0.body")}
              </li>
              <li>
                <strong>{t("privacy.sections.share.items.1.title")}</strong>{" "}
                {t("privacy.sections.share.items.1.body")}
              </li>
              <li>
                <strong>{t("privacy.sections.share.items.2.title")}</strong>{" "}
                {t("privacy.sections.share.items.2.body")}
              </li>
              <li>
                <strong>{t("privacy.sections.share.items.3.title")}</strong>{" "}
                {t("privacy.sections.share.items.3.body")}
              </li>
            </ul>
          </section>

          {/* Retention */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("privacy.sections.retain.heading")}
            </h2>
            <p className="mb-4">{t("privacy.sections.retain.p1")}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t("privacy.sections.retain.items.0")}</li>
              <li>{t("privacy.sections.retain.items.1")}</li>
              <li>{t("privacy.sections.retain.items.2")}</li>
            </ul>
          </section>

          {/* Rights */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("privacy.sections.rights.heading")}
            </h2>
            <p className="mb-4">{t("privacy.sections.rights.p1")}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t("privacy.sections.rights.items.0")}</li>
              <li>{t("privacy.sections.rights.items.1")}</li>
              <li>{t("privacy.sections.rights.items.2")}</li>
              <li>{t("privacy.sections.rights.items.3")}</li>
            </ul>
            <p className="mt-4">
              {t("privacy.sections.rights.contactPreamble")}{" "}
              <a
                href="mailto:support@routedlabs.com"
                className="text-blue-600 underline"
              >
                support@routedlabs.com
              </a>
              .
            </p>
          </section>

          {/* Security */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("privacy.sections.security.heading")}
            </h2>
            <p className="mb-4">{t("privacy.sections.security.p1")}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t("privacy.sections.security.items.0")}</li>
              <li>{t("privacy.sections.security.items.1")}</li>
              <li>{t("privacy.sections.security.items.2")}</li>
              <li>{t("privacy.sections.security.items.3")}</li>
            </ul>
          </section>

          {/* Contact */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("privacy.sections.contact.heading")}
            </h2>
            <p className="mb-4">{t("privacy.sections.contact.p1")}</p>
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {t("privacy.sections.contact.company")}
              </h3>
              <p className="text-gray-700">
                <strong>{t("privacy.sections.contact.emailLabel")}</strong>{" "}
                <a
                  href="mailto:support@routedlabs.com"
                  className="text-blue-600 underline"
                >
                  support@routedlabs.com
                </a>
              </p>
              <p className="text-gray-700">
                <strong>{t("privacy.sections.contact.addressLabel")}</strong>{" "}
                {t("privacy.sections.contact.address")}
              </p>
              <p className="text-gray-700">
                <strong>{t("privacy.sections.contact.subjectLabel")}</strong>{" "}
                {t("privacy.sections.contact.subject")}
              </p>
            </div>
          </section>

          {/* Updates */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("privacy.sections.updates.heading")}
            </h2>
            <p className="mb-4">{t("privacy.sections.updates.p1")}</p>
            <p>{t("privacy.sections.updates.p2")}</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
